export interface Fees {
  tradingFee: number;
  maximumFeePercentage: number;
  minimumFee: number;
  ongoingCharge: number;
  currencyExchangeExpense: number;
  operatingExpense: number;
}

export interface Investment {
  investment: number;
  monthlyInvestment: number;
  years: number;
  annualReturn: number;
}

export interface DataPoint {
  y: number;
  marker: { enabled: boolean };
}

export interface ReturnValues {
  value: number;
  cost: number;
  tradingFee: number;
  ongoingCharge: number;
  profitData: DataPoint[];
  costsData: DataPoint[];
}

const getEtfValueAndCosts = (fees: Fees, investment: Investment): ReturnValues => {
  // Start values
  let value = investment.investment;
  let totalOngoingCharge = 0;
  let totalCurrencyExchangeExpense = 0;
  let totalOperatingExpense = 0;

  // Arrays to store monthly data points
  const profitDataTemp: DataPoint[] = [];
  const costsDataTemp: DataPoint[] = [];

  // Calculate trading fee & check minimum and maximum
  let tradingFee = (value * fees.tradingFee) / 100;
  tradingFee = Math.max(tradingFee, fees.minimumFee);
  const maximumTradingFeeInEuros = (value * fees.maximumFeePercentage) / 100;
  tradingFee = Math.min(tradingFee, maximumTradingFeeInEuros);
  value -= tradingFee;

  // Accumulation per month
  const monthlyReturnRate = investment.annualReturn / 12 / 100;
  const months = investment.years * 12;

  for (let month = 0; month < months; month += 1) {
    // Add the monthly investment
    value += investment.monthlyInvestment;

    // Monthly interest
    value += value * monthlyReturnRate;

    // Monthly ongoing charge
    const ongoingCharge = (value * (fees.ongoingCharge / 100)) / 12;
    value -= ongoingCharge;
    totalOngoingCharge += ongoingCharge;

    // Currency exchange expense
    const currencyExchangeExpense = (value * fees.currencyExchangeExpense) / 100;
    value -= currencyExchangeExpense;
    totalCurrencyExchangeExpense += currencyExchangeExpense;

    // Apply the operating expense once per year (in the last month of each year)
    if ((month + 1) % 12 === 0) {
      const operatingExpense = (value * fees.operatingExpense) / 100;
      value -= operatingExpense;
      totalOperatingExpense += operatingExpense;
    }

    // Determine if it's the first or final month
    const isFirstMonth = month === 0;
    const isFinalMonth = month === months - 1;

    // Store the monthly value and cost with marker on the first and final month
    profitDataTemp.push({
      y: value,
      marker: { enabled: isFirstMonth || isFinalMonth },
    });

    costsDataTemp.push({
      y:
        tradingFee +
        totalOngoingCharge +
        totalCurrencyExchangeExpense +
        totalOperatingExpense,
      marker: { enabled: isFirstMonth || isFinalMonth },
    });
  }

  return {
    value,
    cost:
      tradingFee +
      totalOngoingCharge +
      totalCurrencyExchangeExpense +
      totalOperatingExpense,
    tradingFee,
    ongoingCharge: totalOngoingCharge,
    profitData: profitDataTemp,
    costsData: costsDataTemp,
  };
};

export default getEtfValueAndCosts;
